import * as React from 'react'
import Modal from 'react-modal'
import { connect } from 'react-redux'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'

import { EMAIL, RECIPIENT_GROUP, USER } from '@disco/constants/RecipientTypes'
import {
  createEmailGroup,
  updateEmailGroup,
} from '@disco/state-management/actions/EmailGroupActions'
import { validateEmail } from '@disco/utils/validation'

import type { Recipient } from 'types/list'
import type { State } from 'types/redux'
import type { ValidationProps } from 'types/validation'

import RecipientList from 'components/common/email-groups/recipient-list'
import { Box } from 'components/ds'
import Button from 'components/ui/button'
import Label from 'components/ui/label'
import modalStyles from 'components/utils/modalStyles'

function mapStateToProps(state: State) {
  return { ...state.emailGroups }
}

const mapDispatchToProps = {
  updateEmailGroup,
  createEmailGroup,
}
type Props = {
  recipients?: Array<Recipient>
  onSubmit: (groupName: string) => void
  onClose: () => unknown
  isFetched: boolean
  isDeleting: boolean
  isSaving: boolean
  noRecipientEditing?: boolean
  // always true
  error: Error | null | undefined
  updateEmailGroup: typeof updateEmailGroup
  createEmailGroup: typeof createEmailGroup
} & ValidationProps

/**
 * Edit email group form component for creating and managing mailing lists
 * Provides form validation, recipient management, and server-side group creation
 * Supports recipient validation, spreading recipient groups, and error handling
 * @param recipients - Array of recipients to initialize the form
 * @param onSubmit - Callback when form is successfully submitted
 * @param onClose - Callback to close the form
 * @param isFetched - Whether data has been fetched
 * @param isDeleting - Whether deletion is in progress
 * @param isSaving - Whether saving is in progress
 * @param noRecipientEditing - Whether recipient editing is disabled
 * @param error - Error object if any
 * @param updateEmailGroup - Redux action to update email group
 * @param createEmailGroup - Redux action to create email group
 */
type FormState = {
  error: Error | null | undefined
  name: string
  recipients: Array<Recipient>
}

class EditGroupForm extends React.Component<Props, FormState> {
  state: FormState = {
    error: null,
    name: '',
    recipients: this.props.recipients ? this.props.recipients : [],
  }
  getValidatorData = () => ({
    name: this.state.name,
  })
  validatorTypes = strategy.createSchema(
    {
      name: 'required|max:100',
    },
    {
      'required.name': 'Please enter a name',
    },
  )

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.props.recipients !== nextProps.recipients) {
      this.setState({
        recipients: nextProps.recipients ?? [],
      })
    }
  }

  _onNameChange = e => {
    this.props.clearValidations()
    this.setState({
      name: e.target.value,
    })
  }

  _validateRecipient(recipient, suggestions: Recipient[] = []) {
    let validRecipient = recipient !== null

    if (validRecipient && (recipient.type === EMAIL || recipient.email)) {
      validRecipient = validRecipient && validateEmail(recipient.email)
    }

    // if user conneceted to multiple businesses, check if business_ids is set
    if (
      validRecipient &&
      recipient.type === USER &&
      recipient.businesses &&
      recipient.businesses.length > 1
    ) {
      validRecipient =
        validRecipient &&
        recipient.business_ids &&
        recipient.business_ids.length > 0
    }

    if (recipient && recipient.type === EMAIL) {
      validRecipient =
        validRecipient &&
        !suggestions.find(
          s =>
            s.email === recipient.email &&
            s.type !== EMAIL &&
            s.businesses &&
            s.businesses.length > 1,
        )
    }

    return validRecipient
  }

  _onSubmit = () => {
    this.props.validate(error => {
      if (!error) this._submitToServer()
    })
  }
  _submitToServer = async () => {
    try {
      await this.props.createEmailGroup({
        name: this.state.name,
        recipients: this.state.recipients,
      })
      this.props.onSubmit(this.state.name)
    } catch (err) {
      console.error(err)
      this.setState({
        error: err,
      })
    }
  }
  _deleteRecipient = (recipient, index) => {
    this.setState({
      recipients: [
        ...this.state.recipients.slice(0, index),
        ...this.state.recipients.slice(index + 1),
      ],
    })
  }
  _spreadRecipient = (recipient, index) => {
    if (recipient.type === RECIPIENT_GROUP) {
      this.setState({
        recipients: [
          ...this.state.recipients.slice(0, index),
          ...recipient.recipients,
          ...this.state.recipients.slice(index + 1),
        ],
      })
    }
  }

  _renderModal(formCaption, contents) {
    return (
      <Modal
        contentLabel={formCaption}
        onRequestClose={this.props.onClose}
        isOpen
        style={modalStyles}
      >
        <div className="Form">
          <h1 className="Form_Header">{formCaption}</h1>
          {contents}
        </div>
      </Modal>
    )
  }

  render() {
    let formCaption
    let submitCaption
    let submittingCaption
    formCaption = 'New Mailing List'
    submitCaption = 'Create List'
    submittingCaption = 'Creating List'
    let recipientsList: React.ReactNode = null

    if (this.state.recipients && this.state.recipients.length > 0) {
      recipientsList = (
        <Box mb="4">
          <label className="Form_Label">Recipients</label>
          <RecipientList
            recipients={this.state.recipients}
            onClick={this._deleteRecipient}
            onInplaceEdit={this._spreadRecipient}
          />
        </Box>
      )
    }

    const error = this.state.error
    const submitDisabled = !this.props.isValid()
    return this._renderModal(
      formCaption,
      <React.Fragment>
        <Box mb="4">
          <Label
            caption="Mailing list name"
            required
            validationError={this.props
              .getValidationMessages('name')
              .toString()}
          />
          <input
            className="Form_Input"
            value={this.state.name}
            onChange={this._onNameChange}
            placeholder="Name your group"
          />
        </Box>

        {recipientsList}

        {error ? (
          <div className="Login_ValidationMessage">{error.toString()}</div>
        ) : null}
        <div className="Form_ButtonsContainer separated-small">
          <Button
            onClick={this._onSubmit}
            caption={submitCaption}
            disabled={submitDisabled}
            progressIndicator={this.props.isSaving}
            progressCaption={submittingCaption}
          />
          <Button onClick={this.props.onClose} caption="Cancel" />
        </div>
      </React.Fragment>,
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(validation(strategy)(EditGroupForm)) as any
